import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AdminDashboardService } from '../../core/services/admin-dashboard.service';
import { AdminDashboard } from '../../core/models/admin.models';
import { RouterModule } from '@angular/router';

@Component({
  selector: 'app-admin-dashboard',
  standalone: true,
  imports: [CommonModule, RouterModule],
  template: `
    <div class="space-y-6">
      
      <div class="flex justify-between items-center mb-6">
        <h2 class="text-2xl font-semibold text-gray-800">Dashboard</h2>
        <button (click)="loadDashboard()" class="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition">
          Refresh
        </button>
      </div>

      <!-- Loading Spinner -->
      <div *ngIf="isLoading" class="flex justify-center items-center py-20">
        <div class="w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
      </div>

      <!-- Error State -->
      <div *ngIf="!isLoading && errorMessage" class="bg-red-50 text-red-700 px-4 py-3 rounded-lg">
        {{ errorMessage }}
      </div>

      <ng-container *ngIf="!isLoading && !errorMessage && stats">
        
        <!-- Top Metrics -->
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
          <div class="bg-white p-6 rounded-2xl shadow border-l-4 border-blue-500">
            <h3 class="text-gray-500 text-sm font-medium">Total Users</h3>
            <p class="text-3xl font-bold text-gray-800 mt-2">{{ stats.users }}</p>
          </div>
          <div class="bg-white p-6 rounded-2xl shadow border-l-4 border-purple-500">
            <h3 class="text-gray-500 text-sm font-medium">Total Mechanics</h3>
            <p class="text-3xl font-bold text-gray-800 mt-2">{{ stats.mechanics }}</p>
          </div>
          <div class="bg-white p-6 rounded-2xl shadow border-l-4 border-green-500">
            <h3 class="text-gray-500 text-sm font-medium">Total Bookings</h3>
            <p class="text-3xl font-bold text-gray-800 mt-2">{{ stats.bookings }}</p>
          </div>
          <div class="bg-white p-6 rounded-2xl shadow border-l-4 border-emerald-500">
            <h3 class="text-gray-500 text-sm font-medium">Monthly Revenue</h3>
            <p class="text-3xl font-bold text-gray-800 mt-2">\${{ stats.revenue_this_month }}</p>
          </div>
        </div>

        <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
          
          <!-- Operations & Mechanics -->
          <div class="bg-white p-6 rounded-2xl shadow">
            <h3 class="font-semibold text-gray-800 mb-4 border-b pb-2">Operations & Activity</h3>
            <div class="grid grid-cols-2 gap-4">
               <div class="p-4 bg-blue-50 rounded-lg">
                 <p class="text-sm text-blue-600 font-medium">Mechanics Online</p>
                 <p class="text-2xl font-bold text-blue-900 mt-1">{{ stats.mechanics_online }}</p>
               </div>
               <div class="p-4 bg-orange-50 rounded-lg">
                 <p class="text-sm text-orange-600 font-medium">Pending Verification</p>
                 <p class="text-2xl font-bold text-orange-900 mt-1">{{ stats.mechanics_pending_verify }}</p>
               </div>
               <div class="p-4 bg-indigo-50 rounded-lg">
                 <p class="text-sm text-indigo-600 font-medium">Bookings Today</p>
                 <p class="text-2xl font-bold text-indigo-900 mt-1">{{ stats.bookings_today }}</p>
               </div>
               <div class="p-4 bg-teal-50 rounded-lg">
                 <p class="text-sm text-teal-600 font-medium">Active Bookings</p>
                 <p class="text-2xl font-bold text-teal-900 mt-1">{{ stats.bookings_active }}</p>
               </div>
            </div>
          </div>

          <!-- Alerts & Support -->
          <div class="bg-white p-6 rounded-2xl shadow">
            <h3 class="font-semibold text-gray-800 mb-4 border-b pb-2">Alerts & Support</h3>
            <div class="grid grid-cols-2 gap-4">
               <div class="p-4 bg-red-50 rounded-lg border border-red-100">
                 <div class="flex items-center gap-2 mb-1">
                   <span class="w-2 h-2 rounded-full bg-red-500 animate-pulse"></span>
                   <p class="text-sm text-red-600 font-medium">SOS Alerts Open</p>
                 </div>
                 <p class="text-3xl font-bold text-red-900 mt-1">{{ stats.sos_open }}</p>
               </div>
               <div class="p-4 bg-yellow-50 rounded-lg border border-yellow-100">
                 <p class="text-sm text-yellow-700 font-medium">Support Tickets Open</p>
                 <p class="text-3xl font-bold text-yellow-900 mt-1">{{ stats.tickets_open }}</p>
               </div>
            </div>
          </div>

        </div>

      </ng-container>
    </div>
  `
})
export class AdminDashboardComponent implements OnInit {
  stats: AdminDashboard | null = null;
  isLoading = false;
  errorMessage = '';

  constructor(private dashboardService: AdminDashboardService) {}

  ngOnInit() {
    this.loadDashboard();
  }

  loadDashboard() {
    this.isLoading = true;
    this.errorMessage = '';

    this.dashboardService.getDashboardStats().subscribe({
      next: (data) => {
        this.stats = data;
        this.isLoading = false;
      },
      error: (err) => {
        this.errorMessage = err?.error?.message ?? 'Failed to load dashboard.';
        this.isLoading = false;
      }
    });
  }
}
